/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Else, If, Then } from "react-if";
import {
  Box,
  BrickLoader,
  FlexContainer,
  Modal,
  Spacer,
  theme,
} from "@nordcloud/gnui";
import { useGetNotificationGroupPlansQuery } from "~/generated/graphql";
import { LoaderWrap } from "~/components";
import { showError, showSuccess } from "~/services/toast";
import { inflect } from "~/tools";
import { useDeleteNotificationGroup } from "../../hooks/useDeleteNotificationGroup/useDeleteNotificationGroup";

type Props = {
  notificationGroupId: string;
  notificationGroupName: string;
  isDeleteModalOpen: boolean;
  close: () => void;
  onSuccess?: () => void;
};

export function DeleteNotificationGroup({
  notificationGroupId,
  notificationGroupName,
  isDeleteModalOpen,
  close,
  onSuccess,
}: Props) {
  const [deleteNotificationGroup] = useDeleteNotificationGroup({
    onSuccess: () => {
      close();
      onSuccess?.();
    },
  });

  function deleteGroup(id: string) {
    return deleteNotificationGroup(id)
      .then(() => showSuccess(`Notification group has been deleted.`))
      .catch(() => showError("Something went wrong."));
  }

  function onDelete(id: string) {
    deleteGroup(id);
  }

  const { data, loading } = useGetNotificationGroupPlansQuery({
    variables: { notificationGroupId: notificationGroupId },
  });

  const notificationGroupPlans =
    data?.notificationGroup?.plans?.map((p) => ({
      id: p?.id,
      name: p?.name,
    })) ?? [];

  const usedInPlansCount =
    notificationGroupPlans && notificationGroupPlans.length;
  const isUsedAtPlans = usedInPlansCount > 0;

  return (
    <Modal
      onClose={close}
      isOpen={isDeleteModalOpen}
      actionLabel="Delete"
      contentLabel="Delete Notification Group"
      actions={
        isUsedAtPlans
          ? [
              {
                order: 0,
                onAction: close,
                label: "Cancel",
                severity: "high",
              },
            ]
          : [
              {
                order: 0,
                onAction: () => onDelete(notificationGroupId),
                label: "Delete",
                severity: "high",
              },
            ]
      }
    >
      <LoaderWrap loading={loading} Component={BrickLoader}>
        <If condition={isUsedAtPlans}>
          <Then>
            {`You are trying to delete ${notificationGroupName}, but this notification group is used in the ${inflect(
              "plan"
            )(usedInPlansCount)} below:`}
            <Spacer height={theme.spacing.spacing04} />
            {notificationGroupPlans?.map((planDetail) => (
              <FlexContainer
                key={planDetail.id}
                direction="row"
                justifyContent="space-around"
              >
                <Box boxStyle="lightGrey"> {planDetail.name} </Box>
              </FlexContainer>
            ))}
            <Spacer height={theme.spacing.spacing04} />
            {`If you still want to delete this notification group, you must delete it from the ${inflect(
              "plan"
            )(usedInPlansCount)} first.`}
          </Then>
          <Else>{`Are you sure you want to delete ${notificationGroupName} notification group?`}</Else>
        </If>
      </LoaderWrap>
    </Modal>
  );
}
